import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * DepressionModeContext - おやすみモードのグローバル管理
 *
 * 機能:
 * - 全ページでおやすみモード状態を共有（3段階）
 * - localStorageで永続化
 * - body要素にクラス追加でグローバルスタイル適用
 * - ページごとに異なる配慮を実装（主要コンテンツ/つむぎchat）
 *
 * 設計方針:
 * - レベル0: 通常モード - すべての機能
 * - レベル1: おやすみモード - 機能削減 + 見た目の配慮
 * - レベル2: 超おやすみモード - つむぎとの会話以外すべて非表示
 */

const DepressionModeContext = createContext();

export const useDepressionMode = () => {
  const context = useContext(DepressionModeContext);
  if (!context) {
    throw new Error('useDepressionMode must be used within DepressionModeProvider');
  }
  return context;
};

export const DepressionModeProvider = ({ children }) => {
  const [depressionLevel, setDepressionLevel] = useState(() => {
    // localStorageから初期値を取得（0: 通常, 1: おやすみ, 2: 超おやすみ）
    const stored = localStorage.getItem('depressionLevel');
    return stored ? parseInt(stored, 10) : 0;
  });

  // 後方互換性のため
  const depressionMode = depressionLevel > 0;
  const ultraDepressionMode = depressionLevel === 2;

  // おやすみモードレベル切り替え
  const setDepressionModeLevel = (level) => {
    console.log('📝 Context setDepressionModeLevel called:', {
      currentLevel: depressionLevel,
      newLevel: level
    });

    setDepressionLevel(level);
    localStorage.setItem('depressionLevel', level.toString());

    console.log('💾 localStorage updated:', localStorage.getItem('depressionLevel'));

    if (level === 0) {
      console.log('✅ 通常モード');
    } else if (level === 1) {
      console.log('✅ おやすみモード');
    } else if (level === 2) {
      console.log('✅ 超おやすみモード');
    }
  };

  // 後方互換性のための関数
  const toggleDepressionMode = (enabled) => {
    setDepressionModeLevel(enabled ? 1 : 0);
  };

  // body要素にクラスを追加/削除（グローバルスタイル用）
  useEffect(() => {
    // 既存のクラスを削除
    document.body.classList.remove('depression-mode', 'ultra-depression-mode');

    // レベルに応じてクラスを追加
    if (depressionLevel === 1) {
      document.body.classList.add('depression-mode');
    } else if (depressionLevel === 2) {
      document.body.classList.add('depression-mode', 'ultra-depression-mode');
    }

    return () => {
      document.body.classList.remove('depression-mode', 'ultra-depression-mode');
    };
  }, [depressionLevel]);

  const value = {
    depressionMode,
    depressionLevel,
    ultraDepressionMode,
    toggleDepressionMode,
    setDepressionModeLevel,
  };

  return (
    <DepressionModeContext.Provider value={value}>
      {children}
    </DepressionModeContext.Provider>
  );
};
